"use client";

import { RefObject, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PortfolioApiQuestion } from "@/type/servicesPageTypes";
import Icon from "@/components/icons/Icons";
import { useIsMobile } from "@/utils/useIsMobile";
import { getFinancialPlanningBasePath } from "@/utils/financialPageFunctions";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { useDispatch } from "react-redux";
import { openHelpModal } from "@/redux/features/HelpModal/helpModalSlice";

interface StepItem {
  id: number;
  title: string;
  questions: PortfolioApiQuestion[];
}

interface Props {
  groupedSteps: StepItem[];
  mainTitle: string;
  currentStep: number;
  setCurrentStep: (stepId: number) => void;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  skippedSteps?: number[];
}

export default function StepperSidebar({
  groupedSteps,
  mainTitle,
  currentStep,
  setCurrentStep,
  scrollContainerRef,
  skippedSteps = [],
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const pathname = usePathname();
  const basePath = getFinancialPlanningBasePath(pathname);

  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const smallDevice = useIsMobile(1025);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const activeStep =
    groupedSteps && groupedSteps.length > 0
      ? groupedSteps.find((item) => item.id === currentStep) || groupedSteps[0]
      : null;

  const completedPercentage =
    groupedSteps && groupedSteps.length > 1 ? ((currentStep - 1) / (groupedSteps.length - 1)) * 100 : 0;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleStepChange = (stepId: number) => {
    setCurrentStep(stepId);

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }

    setIsOpen(false);
  };

  return (
    <div className="relative w-full max-w-194.25 lg:max-w-64 xl:max-w-400 mx-auto lg:mx-0">
      {/* Desktop Heading */}
      <h1 className="hidden lg:block text-xl xl:text-2xl font-semibold text-white border-b-2 border-dashed border-white pb-2 mb-6">
        {mainTitle}
      </h1>

      {!isMounted || !groupedSteps || groupedSteps.length === 0 || !activeStep ? (
        <>
          {/* Skeleton */}
          <div className="relative p-4 pb-0 md:p-0">
            <div className="flex w-full items-center overflow-hidden rounded-full border border-primaryBlue/20 bg-white px-4 py-2.5 xs:py-3.5">
              <div className="relative w-full flex items-center gap-2 overflow-hidden">
                <div className="h-4 w-8 bg-gray-200 rounded-md animate-pulse" />
                <div className="h-4 w-9/12 bg-gray-200 rounded-md animate-pulse" />
              </div>
              <Icon.dropdownDownArrow className="shrink-0" />
            </div>
          </div>

          {/* Desktop Steps List Skeleton */}
          <div className="hidden md:flex flex-col gap-2 px-5 py-8 sm:p-6">
            {Array.from({ length: 4 }).map((_item, index) => {
              const isActive = index === 0;
              return (
                <div
                  key={index}
                  className={`
                  relative flex items-center gap-3 text-left
                  h-14 px-3 rounded-xl transition
                  ${isActive ? "bg-white/15 border border-white/10" : "opacity-60"}
                `}
                >
                  <div className="flex h-7 w-7 lg:h-9 lg:w-9 shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 text-2xs lg:text-sm text-white font-medium" />

                  <div className="h-4 w-3/4 bg-white/30 rounded-md animate-pulse" />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div ref={dropdownRef} className="relative">
          {/* Trigger */}
          <div className="flex lg:hidden items-center gap-2 lg:gap-0">
            <button
              type="button"
              onClick={() => {
                if (smallDevice) {
                  setIsOpen((prev) => !prev);
                }
              }}
              className="flex ddd w-full items-center overflow-hidden rounded-full border border-primaryBlue/20 bg-white lg:pointer-events-none"
            >
              <div className="relative flex h-full flex-1 items-center gap-2 overflow-hidden px-4 py-2.5 md:py-3.5">
                <div
                  className="absolute inset-y-0 left-0 bg-[#ADFA95] transition"
                  style={{
                    width: `${completedPercentage}%`,
                  }}
                />

                <div className="relative z-1 flex items-center gap-2">
                  <span className="text-sm leading-1.2 text-primaryBlack">
                    <span className="text-[#004ac0]">{currentStep}</span>/{groupedSteps.length}
                  </span>

                  <span className="truncate text-sm leading-1.2 text-primaryBlack">- {activeStep.title}</span>
                </div>
              </div>

              <div className="flex h-full w-12 items-center justify-center bg-white">
                <Icon.dropdownDownArrow className={`transition ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>

            <button
              onClick={() => dispatch(openHelpModal())}
              className="shrink-0 flex lg:hidden items-center justify-center rounded-full cursor-pointer w-10 h-10 bg-[#7696ca]"
            >
              <Icon.callIconV2 className="text-white w-4.5 h-4.5" />
            </button>

            <Link
              href={basePath}
              aria-label="Cross The Modal Button"
              className="shrink-0 flex lg:hidden items-center justify-center rounded-full cursor-pointer w-10 h-10 bg-[#7696ca]"
            >
              <Icon.crossIcon className="text-white w-4.5 h-4.5" />
            </Link>
          </div>

          {/* List Wrapper */}
          <AnimatePresence>
            {(isOpen || !smallDevice) && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className={[
                  "absolute inset-x-0 top-[calc(100%+10px)] z-1 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg",
                  "lg:static lg:left-auto lg:right-auto lg:top-auto lg:block lg:overflow-visible lg:rounded-none lg:border-none lg:bg-transparent lg:shadow-none",
                ].join(" ")}
              >
                <div className="relative flex flex-col lg:gap-2">
                  {/* Desktop Active Background */}
                  <motion.div
                    className="absolute inset-x-0 hidden h-14 rounded-xl border border-white bg-white/20 lg:block"
                    animate={{
                      y: groupedSteps.findIndex((item) => item.id === currentStep) * 64,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 28,
                    }}
                  />

                  {groupedSteps.map((stepItem, index) => {
                    const isActive = stepItem.id === currentStep;
                    const isCompleted = currentStep > stepItem.id;
                    const isSkipped = skippedSteps.includes(stepItem.id);

                    return (
                      <button
                        key={stepItem.id}
                        type="button"
                        onClick={() => {
                          const isPreviousOrCurrent = stepItem.id <= currentStep;

                          if (!isPreviousOrCurrent || isSkipped) return;

                          handleStepChange(stepItem.id);
                        }}
                        className={[
                          "relative flex items-center gap-2 xl:gap-3 text-left transition w-full rounded-xl border-b border-[#F1F1F1] px-4 py-3 last:border-b-0 lg:h-14 lg:border-none lg:px-3 lg:py-0",
                          isActive ? "bg-primaryGreen lg:bg-transparent" : "",
                          isSkipped
                            ? "opacity-30 cursor-not-allowed"
                            : isActive
                              ? "font-medium"
                              : isCompleted
                                ? "font-semibold"
                                : "opacity-80",
                        ].join(" ")}
                      >
                        <div
                          className={[
                            "flex items-center justify-center h-7 w-7 xl:h-8 xl:w-8 text-2xs xl:text-sm shrink-0 rounded-full border transition",
                            isActive
                              ? "border-primaryGreen bg-white text-primaryGreen"
                              : isCompleted
                                ? "border-primaryGreen bg-primaryGreen lg:border-[#22DF00] lg:bg-[#22DF00] text-white"
                                : "text-primaryGreen/50 border-primaryGreen/50 lg:text-white/50 lg:border-white/50",
                          ].join(" ")}
                        >
                          {isCompleted ? <Icon.rightSignIcon className="h-3.5 w-3.5" /> : index + 1}
                        </div>

                        <span
                          className={`text-2sm leading-1.2 xl:leading-1.3 transition xl:text-base line-clamp-3 ${isActive ? "font-semibold text-white" : isCompleted ? "text-primaryGreen/85 lg:text-white/85" : "text-primaryGreen/60 lg:text-white"}`}
                        >
                          {stepItem.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
